import { ComponentBase } from "@abstracts/component-base";
import {
  Component,
  Injector,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  AfterViewInit
} from "@angular/core";
import { Chart, registerables } from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import AnnotationPlugin from "chartjs-plugin-annotation";
import { SankeyController, Flow } from 'chartjs-chart-sankey';
import { EnergyMonitorData } from "@dto/energyMonitorData";
import { KwhPipe } from '../../pipes/kwh.pipe';
import { EnergyMeterConnectedDeviceType } from '../../enums/EnergyMeterConnectedDeviceType.enum';

Chart.register(...registerables);
Chart.register(SankeyController, Flow);

@Component({
  selector: "energy-flow-chart",
  templateUrl: "./energy-flow-chart.component.html",
})
export class EnergyFlowChartComponent
  extends ComponentBase
  implements OnInit, OnChanges, OnDestroy, AfterViewInit
{
  public className: string = "EnergyFlowChart";

  @Input() data: any;
  @Input() date: Date;
  @Input() isLoading: boolean = true;

  public chart: Chart;
  public canvas: any;
  public ctx: any;

  public totalAmount: number;
  public max: number;
  public min: number;
  public average: number;
  public maxShow: boolean = false;
  public minShow: boolean = false;
  public isReadyForChart: boolean = false;
  public isShowingAverage: boolean = false;
  public defaultRange: string;

  constructor(injector: Injector) {
    super(injector);
  }

  ngOnInit(): void {
    super.ngOnInit();
  }

  ngAfterViewInit() {
    this.isReadyForChart = true;
  }

  ngOnChanges(changes: any): void {
    if (!this.isReadyForChart) return;

    if (changes['data'] && this.data) {
      if (!this.chart) {
        this.createChart();
      } else {
        this.updateChart();
      }
    }
  }

  ngOnDestroy(): void {
    this.chart?.destroy();
  }

  private initDatasets() {
    this.chart.data.datasets = [];
    console.log(this.data)

    // for (const data of this.data.flowData) {
    //   const dataset = {
    //     data: data.flow,
    //     colorFrom: data.fromColor,
    //     colorTo: data.toColor,
    //     colorMode: 'gradient',
    //     labels: data.labels,
    //     column: {},
    //     size: 'max'
    //   }
    //   this.addDataset(dataset);
    // }

    const data = this.prepareDataForSankey(this.data);

    const dataset = {
      data: data.flows,
      colorFrom: (c) => c.raw.fromColor,
      colorTo: (c) => c.raw.toColor,
      colorMode: 'gradient',
      labels: data.labels,
      priority: data.priorities,
      column: {},
      border: 'transparent',
      size: 'max'
    }
    
    this.addDataset(dataset);
    console.log(dataset);
  }

  private prepareDataForSankey(data) {
    const flows = [];
    const labels = {};
    const sizes = {};
    const priorities = {};
  
    const energySources = [];
    const energySinks = [];
    const energyStores = [];
    
    for (const meter of data.distributionData) {
      const type = meter.energyMeter.energyMeterType.connectedDeviceType;
      if (type === "supply" || type === "pv") {
        energySources.push(meter);
      } else if (type === "chargingPark") {
        energySinks.push(meter);
      } else if (type === "battery") {
        energySources.push(meter);
        energySinks.push(meter);
      }
    }
  
    for (const source of energySources) {
      const sourceName = source.energyMeter.name;
      labels[sourceName] = sourceName;
      sizes[sourceName] = Math.abs(source.wh);
      priorities[sourceName] = 1;
    }
  
    for (const sink of energySinks) {
      const sinkName = sink.energyMeter.name;
      labels[sinkName] = sinkName;
      sizes[sinkName] = Math.abs(sink.wh);
      priorities[sinkName] = 2;
    }
  
    for (const store of energyStores) {
      const storeName = store.energyMeter.name;
      labels[storeName] = storeName;
      sizes[storeName] = Math.abs(store.wh);
      priorities[storeName] = 3;
    }
  
    for (const sink of energySinks) {
      for (const source of energySources) {
        if (source.energyMeter.name === sink.energyMeter.name || Math.abs(sink.wh) === 0) continue;
        flows.push({
          from: source.energyMeter.name,
          to: sink.energyMeter.name,
          flow: Math.abs(source.wh) * Math.abs(sink.wh),  
          fromColor: source.energyMeter.energyMeterType.colorCodeInHex,
          toColor: sink.energyMeter.energyMeterType.colorCodeInHex
        });

        if (source.energyMeter.connectedDeviceType === EnergyMeterConnectedDeviceType.PV) {
          flows.push({
            from: source.energyMeter.name,
            to: sink.energyMeter.name,
            flow: Math.abs(source.wh) * Math.abs(sink.wh),  
            fromColor: source.energyMeter.energyMeterType.colorCodeInHex,
            toColor: sink.energyMeter.energyMeterType.colorCodeInHex
          });
        }
      }
    }
  
    for (const store of energyStores) {
      for (const source of energySources) {
        if (source.energyMeter.name === store.energyMeter.name || Math.abs(store.wh) === 0) continue;
        flows.push({
          from: source.energyMeter.name,
          to: store.energyMeter.name,
          flow: Math.abs(source.wh) * Math.abs(store.wh),  
          fromColor: source.energyMeter.energyMeterType.colorCodeInHex,
          toColor: store.energyMeter.energyMeterType.colorCodeInHex
        });
      }
    }
  
    return { flows, labels, sizes, priorities };
  }

  private addDataset(dataset) {
    this.chart.data.datasets.push(dataset);
    this.chart?.update();
  }

  private createChart() {
    this.canvas = document.getElementById("energy-flow-chart-canvas");
    this.ctx = this.canvas.getContext("2d");
    
    Chart.register(AnnotationPlugin);
    Chart.register(SankeyController, Flow);
    
    this.chart = new Chart(this.ctx, {
      plugins: [ChartDataLabels],
      type: "sankey",
      data: {
        datasets: [],
      },
      options: {
        plugins: {
          datalabels: {
            color: '#000000',
            font: {
              weight: 'bolder',
              size: 14,
            },
            formatter: (value) => {
              if (isNaN(value)) return "";
              const pipe = new KwhPipe();
              return `${pipe.transform(value, 1)} kWh`;
            },
          },
          legend: {
            labels: {
              color: "#212529",
              font: {
                size: 14,
                family: "Lato",
              },
            },
          },
          tooltip: {
            enabled: false,
          },
        },
      },
    });

    this.updateChart();
  }

  private updateChart() {
    this.initDatasets();
    this.chart.update();
  }
}
